<template>
  <hs-modal
    v-if="isOpen"
    :model-value="isOpen"
    title="Request Log Details"
    description="Inspect request metadata, response timing, and operational context."
    size="lg"
    @close="$emit('close')"
  >
    <!-- Request Information -->
    <div v-if="requestLog" class="request-log-details">
      <!-- Request Details -->
      <section class="request-log-details__section">
        <h4>
          Request Information
        </h4>
        <div class="request-log-details__grid">
          <div class="request-log-details__item">
            <span>
              Method
            </span>
            <strong>
              <hs-badge :tone="getMethodTone(requestLog.method)" size="sm">
                {{ requestLog.method || "N/A" }}
              </hs-badge>
            </strong>
          </div>
          <div class="request-log-details__item">
            <span>
              Endpoint
            </span>
            <strong class="request-log-details__mono">
              {{ requestLog.endpoint || "N/A" }}
            </strong>
          </div>
          <div class="request-log-details__item">
            <span>
              User Agent
            </span>
            <strong>
              {{ requestLog.user_agent || "N/A" }}
            </strong>
          </div>
          <div class="request-log-details__item">
            <span>
              Request Reference
            </span>
            <strong class="request-log-details__mono">
              {{ requestLog.request_ref || "N/A" }}
            </strong>
          </div>
        </div>
      </section>

      <!-- Response Details -->
      <section class="request-log-details__section">
        <h4>
          Response Information
        </h4>
        <div class="request-log-details__grid">
          <div class="request-log-details__item">
            <span>
              Status Code
            </span>
            <strong>
              <hs-badge :tone="getStatusTone(requestLog.status_code)" size="sm">
                {{ requestLog.status_code || "N/A" }}
              </hs-badge>
            </strong>
          </div>
          <div class="request-log-details__item">
            <span>
              Duration
            </span>
            <strong>
              {{ requestLog.duration_ms }}ms
            </strong>
          </div>
        </div>
      </section>

      <!-- Context Information -->
      <section class="request-log-details__section">
        <h4>
          Context Information
        </h4>
        <div class="request-log-details__grid">
          <div class="request-log-details__item">
            <span>
              User ID
            </span>
            <strong>
              {{ requestLog.user_id || "Anonymous" }}
            </strong>
          </div>
          <div class="request-log-details__item">
            <span>
              Store ID
            </span>
            <strong>
              {{ requestLog.store_id || "N/A" }}
            </strong>
          </div>
        </div>
      </section>

      <!-- Timing Information -->
      <section class="request-log-details__section">
        <h4>
          Timing Information
        </h4>
        <div class="request-log-details__grid">
          <div class="request-log-details__item">
            <span>
              Created At
            </span>
            <strong>
              {{ formatDateTime(requestLog.created_at) }}
            </strong>
          </div>
          <div class="request-log-details__item">
            <span>
              Time Ago
            </span>
            <strong>
              {{ formatTimeAgo(requestLog.created_at) }}
            </strong>
          </div>
        </div>
      </section>
    </div>

    <template #footer>
      <hs-button variant="secondary" @click="$emit('close')">Close</hs-button>
    </template>
  </hs-modal>
</template>

<script>
import { HsBadge, HsButton, HsModal } from "@/components/ui";

export default {
  name: "RequestLogDetailsModal",
  components: {
    HsBadge,
    HsButton,
    HsModal,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    requestLog: {
      type: Object,
      default: null,
    },
  },
  emits: ["close"],
  setup() {
    const getMethodTone = (method) => {
      switch (method) {
        case "GET":
          return "info";
        case "POST":
          return "success";
        case "PUT":
          return "warning";
        case "PATCH":
          return "primary";
        case "DELETE":
          return "danger";
        default:
          return "neutral";
      }
    };

    const getStatusTone = (statusCode) => {
      if (statusCode >= 200 && statusCode < 300) {
        return "success";
      } else if (statusCode >= 300 && statusCode < 400) {
        return "info";
      } else if (statusCode >= 400 && statusCode < 500) {
        return "warning";
      } else if (statusCode >= 500) {
        return "danger";
      }
      return "neutral";
    };

    const formatDateTime = (isoString) => {
      if (!isoString) return "N/A";
      return new Date(isoString).toLocaleString();
    };

    const formatTimeAgo = (isoString) => {
      if (!isoString) return "N/A";

      const now = new Date();
      const time = new Date(isoString);
      const diffMs = now - time;
      const diffMins = Math.floor(diffMs / (1000 * 60));

      if (diffMins < 1) return "just now";
      if (diffMins < 60) return `${diffMins} min ago`;

      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24)
        return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;

      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    };

    return {
      getMethodTone,
      getStatusTone,
      formatDateTime,
      formatTimeAgo,
    };
  },
};
</script>

<style scoped>
.request-log-details {
  display: grid;
  gap: 1rem;
}

.request-log-details__section {
  background: color-mix(in srgb, var(--hs-bg-soft, #f1f5f9) 65%, var(--hs-surface, #ffffff));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  padding: 1rem;
}

.request-log-details__section h4 {
  color: var(--hs-text, #0f172a);
  font-size: 0.95rem;
  font-weight: 950;
  margin: 0 0 0.85rem;
}

.request-log-details__grid {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.request-log-details__item {
  min-width: 0;
}

.request-log-details__item span {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.06em;
  margin-bottom: 0.28rem;
  text-transform: uppercase;
}

.request-log-details__item strong {
  color: var(--hs-text, #0f172a);
  display: block;
  font-size: 0.88rem;
  font-weight: 850;
  overflow-wrap: anywhere;
}

.request-log-details__mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
}

@media (max-width: 640px) {
  .request-log-details__grid {
    grid-template-columns: 1fr;
  }
}
</style>
